/**
 * Universal Badge Styles - Single Source of Truth for Shop Services Module
 * 
 * ⭐ UNIVERSAL BADGE SYSTEM - Change badge styles here to update ALL Shop Services badges at once
 * 
 * ⚠️ SCOPED TO SHOP SERVICES ONLY - Uses .shop- prefix to avoid affecting other modules
 * - Uses .shop-status-badge and .shop-fulfillment-badge classes (NOT generic .status-badge)
 * - Completely isolated from TournamentCard.css and other badge styles
 * - All badge styling comes from BadgeStyles.tsx and BadgeStyles.css ONLY
 * 
 * Other modules (Tournaments, etc.) use their own badge implementations and are NOT affected
 */

import React from 'react';
import { Badge } from 'react-bootstrap';
import './BadgeStyles.css';

// Badge color constants - Shop Services Module Only
// ⭐ SINGLE SOURCE OF TRUTH - Update badge colors here to change Shop Services badges
// These colors are applied directly via inline styles in the badge components
export const BADGE_COLORS = {
  // Status Badges
  STATUS: {
    ACTIVE: {
      backgroundColor: '#D9E7CD', // Light green background
      color: '#2D7A2D', // Dark green text
      borderColor: 'transparent', 
    },
    INACTIVE: {
      backgroundColor: '#FBDAD5', // Light red background
      color: '#D73027', // Dark red text
      borderColor: 'transparent', 
    },
  },
  // Fulfillment Badges
  FULFILLMENT: {
    PICKUP: {
      backgroundColor: 'rgba(253, 150, 68, 0.2)', // Light orange background (20% opacity)
      color: '#FD9644', // Orange text
      borderColor: '#FD9644', // Orange border (matches text color)
    },
    DELIVERY: {
      backgroundColor: 'rgba(115, 87, 235, 0.2)', // Light purple background (20% opacity)
      color: '#7357EB', // Purple text
      borderColor: '#7357EB', // Purple border (matches text color)
    },
    EXTERNAL_ADS: {
      backgroundColor: 'rgba(37, 99, 235, 0.2)', // Light blue background
      color: '#2563EB', // Blue text
      borderColor: '#2563EB', // Blue border
    },
  },
  // Location Badges
  LOCATION: {
    backgroundColor: '#F1EEFD', // Light purple background
    color: '#7357EB', // Purple text
    borderColor: '#7357EB', // Purple border (matches text color)
  },
  // Product Badges
  PRODUCT: {
    BEST_SELLER: {
      backgroundColor: '#F1EEFD', // Light purple background to match requirement
      color: '#7357EB', // Purple text to match requirement
      borderColor: '#7357EB', // Purple border to match requirement
    },
    NEW: {
      backgroundColor: '#F1EEFD', // Light purple background to match requirement
      color: '#7357EB', // Purple text to match requirement
      borderColor: '#7357EB', // Purple border to match requirement
    },
    FEATURED: {
      backgroundColor: '#F1EEFD', // Light purple background to match requirement
      color: '#7357EB', // Purple text to match requirement
      borderColor: '#7357EB', // Purple border to match requirement
    },
    TOP_PICKS: {
      backgroundColor: '#F1EEFD',
      color: '#7357EB',
      borderColor: '#7357EB',
    },
    TRENDING: {
      backgroundColor: '#F1EEFD',
      color: '#7357EB',
      borderColor: '#7357EB',
    },
  },
  // Selected Colors Badge (matches Product Variants > Selected Colors)
  SELECTED_COLORS: {
    backgroundColor: '#E6F1FF', // Light blue background (same as Selected Colors)
    color: '#006EFF', // Blue text (same as Selected Colors)
    borderColor: '#006EFF', // Blue border (same as Selected Colors)
  },
  // Return Badge (custom colors as requested)
  RETURN: {
    backgroundColor: '#FFF4DF', // Light yellow/cream background
    color: '#735E37', // Brown text
    borderColor: '#735E37', // Brown border
  },
  
  // Promo Status Badges (for Promos module)
  PROMO: {
    DRAFT: {
      backgroundColor: '#F9FAFB',
      color: '#374151',
      borderColor: '#D1D5DB',
    },
    ACTIVE: {
      backgroundColor: '#D9E7CD',
      color: '#2D7A2D',
      borderColor: 'transparent',
    },
    PAUSED: {
      backgroundColor: '#FEF3C7',
      color: '#D97706',
      borderColor: '#D97706',
    },
    ARCHIVED: {
      backgroundColor: '#FBDAD5',
      color: '#D73027',
      borderColor: '#D73027',
    },
    EXPIRED: {
      backgroundColor: '#F3F4F6',
      color: '#6B7280',
      borderColor: '#6B7280',
    },
    INACTIVE: {
      backgroundColor: '#FBDAD5',
      color: '#D73027',
      borderColor: 'transparent',
    },
  },
  
  // Campaign Status Badges (for Promos module)
  CAMPAIGN: {
    DRAFT: {
      backgroundColor: '#F9FAFB', // Very light gray background
      color: '#374151', // Dark gray text
      borderColor: '#D1D5DB',
    },
    ACTIVE: {
      backgroundColor: '#D9E7CD', // Light green background (same as STATUS.ACTIVE)
      color: '#2D7A2D', // Dark green text
      borderColor: 'transparent',
    },
    PAUSED: {
      backgroundColor: '#FEF3C7', // Light yellow/orange background
      color: '#D97706', // Orange text
      borderColor: '#D97706',
    },
    ARCHIVED: {
      backgroundColor: '#FBDAD5', // Light red background
      color: '#D73027', // Dark red text
      borderColor: '#D73027',
    },
    EXPIRED: {
      backgroundColor: '#F3F4F6', // Light gray background
      color: '#6B7280', // Gray text
      borderColor: '#6B7280',
    },
    SCHEDULED: {
      backgroundColor: '#E6F1FF', // Light blue background (same as SELECTED_COLORS)
      color: '#006EFF', // Blue text
      borderColor: '#006EFF',
    },
    INACTIVE: {
      backgroundColor: '#FBDAD5', // Light red background (same as STATUS.INACTIVE)
      color: '#D73027', // Dark red text
      borderColor: 'transparent',
    },
  },
  
  // Campaign Type Badges
  CAMPAIGN_TYPE: {
    FESTIVAL: {
      backgroundColor: '#FEF3C7', // Light yellow background
      color: '#D97706', // Orange text
      borderColor: '#D97706',
    },
    SEASONAL: {
      backgroundColor: '#DBEAFE', // Light blue background
      color: '#2563EB', // Blue text
      borderColor: '#2563EB',
    },
    PERFORMANCE: {
      backgroundColor: '#F3E8FF', // Light purple background
      color: '#7C3AED', // Purple text
      borderColor: '#7C3AED',
    },
    PARTNER: {
      backgroundColor: '#FEE2E2', // Light red background
      color: '#DC2626', // Red text
      borderColor: '#DC2626',
    },
    INTERNAL: {
      backgroundColor: '#E5E7EB', // Light gray background
      color: '#374151', // Dark gray text
      borderColor: '#374151',
    },
  },
  
  // Audit Log Action Badges (for Audit Logs module)
  AUDIT_LOG_ACTION: {
    CREATED: {
      backgroundColor: '#DBEAFE', // Light blue background
      color: '#2563EB', // Blue text
      borderColor: '#2563EB',
    },
    UPDATED: {
      backgroundColor: '#F3F4F6', // Light gray background
      color: '#6B7280', // Gray text
      borderColor: '#6B7280',
    },
    PAUSED: {
      backgroundColor: '#FEF3C7', // Light yellow background
      color: '#D97706', // Orange text
      borderColor: '#D97706',
    },
    ACTIVATED: {
      backgroundColor: '#D9E7CD', // Light green background
      color: '#2D7A2D', // Dark green text
      borderColor: '#2D7A2D',
    },
    EXPIRED: {
      backgroundColor: '#FED7AA', // Light orange background
      color: '#EA580C', // Orange text
      borderColor: '#EA580C',
    },
    DELETED: {
      backgroundColor: '#FEE2E2', // Light red background
      color: '#DC2626', // Red text
      borderColor: '#DC2626',
    },
    INACTIVATED: {
      backgroundColor: '#FBDAD5', // Light red background
      color: '#D73027', // Dark red text
      borderColor: '#D73027',
    },
  },
} as const;

interface StatusBadgeProps {
  status: 'ACTIVE' | 'INACTIVE';
  className?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, className = '' }) => {
  // Display text: "Active" or "Inactive" (capitalized, not all caps)
  const displayText = status === 'ACTIVE' ? 'Active' : 'Inactive';
  const colors = BADGE_COLORS.STATUS[status];
  return (
    <Badge
      bg="" // Prevent Bootstrap from applying default bg-* classes
      className={`shop-status-badge shop-status-${status.toLowerCase()} ${className}`}
      style={{
        backgroundColor: colors.backgroundColor,
        color: colors.color,
        borderColor: colors.borderColor,
        border: `1px solid ${colors.borderColor}`,
      } as React.CSSProperties}
    >
      {displayText}
    </Badge>
  );
};

interface FulfillmentBadgeProps {
  capability: 'PICKUP' | 'DELIVERY' | 'EXTERNAL_ADS';
  className?: string;
}

const FULFILLMENT_DISPLAY_TEXT: Record<'PICKUP' | 'DELIVERY' | 'EXTERNAL_ADS', string> = {
  PICKUP: 'Pickup',
  DELIVERY: 'Delivery',
  EXTERNAL_ADS: 'External Ads',
};

export const FulfillmentBadge: React.FC<FulfillmentBadgeProps> = ({ capability, className = '' }) => {
  const colors = BADGE_COLORS.FULFILLMENT[capability];
  const displayText = FULFILLMENT_DISPLAY_TEXT[capability];
  return (
    <Badge
      bg="" // Prevent Bootstrap from applying default bg-* classes
      className={`shop-fulfillment-badge shop-fulfillment-${capability.toLowerCase()} ${className}`}
      style={{
        backgroundColor: colors.backgroundColor,
        color: colors.color,
        borderColor: colors.borderColor,
        border: `1px solid ${colors.borderColor}`,
        fontFamily: 'Inter, Helvetica Neue, Arial, sans-serif',
        fontWeight: 400,
        fontSize: '14px', // Using Selected Sizes font size instead of 12px
        lineHeight: '20px',
        textAlign: 'center',
        textTransform: 'none',
        minWidth: '70px',
        height: '32px',
        paddingLeft: '12px',
        paddingRight: '12px',
        paddingTop: '6px',
        paddingBottom: '6px',
        borderRadius: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
        marginRight: '8px',
        marginBottom: '8px'
      } as React.CSSProperties}
    >
      {displayText}
    </Badge>
  );
};

interface TypeBadgeProps {
  type: 'ACADEMY' | 'WAREHOUSE';
  className?: string;
}

export const TypeBadge: React.FC<TypeBadgeProps> = ({ type, className = '' }) => {
  // Display text: "Academy" or "Warehouse" (capitalized)
  const displayText = type === 'ACADEMY' ? 'Academy' : 'Warehouse';
  return (
    <Badge
      className={`shop-type-badge shop-type-${type.toLowerCase()} ${className}`}
    >
      {displayText}
    </Badge>
  );
};

interface OrderStatusBadgeProps {
  status: 'CONFIRMED' | 'READY_FOR_PICKUP' | 'DELIVERED' | 'RETURN' | 'REPLACEMENT' | 'CANCELLED' | 'SHIPPED' | 'CREATED' | 'PICKED_UP';
  className?: string;
}

export const OrderStatusBadge: React.FC<OrderStatusBadgeProps> = ({ status, className = '' }) => {
  // Display text mapping
  const displayTextMap: Record<OrderStatusBadgeProps['status'], string> = {
    CONFIRMED: 'Confirmed',
    READY_FOR_PICKUP: 'Ready for Pickup',
    DELIVERED: 'Delivered',
    RETURN: 'Return',
    REPLACEMENT: 'Replacement',
    CANCELLED: 'Cancelled',
    SHIPPED: 'Shipped',
    CREATED: 'Created',
    PICKED_UP: 'Picked Up',
  };
  
  // Map order statuses to existing badge colors from single source of truth
  const getStatusColors = (status: OrderStatusBadgeProps['status']) => {
    switch (status) {
      case 'CONFIRMED':
        // Confirmed → Same as Selected Colors from Product Variants (blue theme)
        return BADGE_COLORS.SELECTED_COLORS;
      case 'DELIVERED':
        // Delivered → Same as Active badge (green theme)
        return BADGE_COLORS.STATUS.ACTIVE;
      case 'READY_FOR_PICKUP':
        // Ready for Pickup → Same as Fulfillment Delivery (purple theme)
        return BADGE_COLORS.FULFILLMENT.DELIVERY;
      case 'REPLACEMENT':
        // Replacement → Same as Inactive badge (red theme)
        return BADGE_COLORS.STATUS.INACTIVE;
      case 'RETURN':
        // Return → Custom colors (brown text, cream background)
        return BADGE_COLORS.RETURN;
      case 'PICKED_UP':
        // Picked Up → Similar to Active but distinct (could use green)
        return BADGE_COLORS.STATUS.ACTIVE;
      case 'CREATED':
        // Created → Use Product colors (purple theme)
        return BADGE_COLORS.PRODUCT.NEW;
      case 'CANCELLED':
        // Cancelled → Use Inactive colors (red theme)
        return BADGE_COLORS.STATUS.INACTIVE;
      case 'SHIPPED':
        // Shipped → Use Delivery colors (purple theme)
        return BADGE_COLORS.FULFILLMENT.DELIVERY;
      default:
        // Default fallback - bright red to make it obvious if status doesn't match
        return {
          backgroundColor: '#ffcdd2',
          color: '#d32f2f',
          borderColor: '#d32f2f'
        };
    }
  };
  
  const displayText = displayTextMap[status] || status;
  const statusClass = status.toLowerCase().replace('_', '-');
  const colors = getStatusColors(status);
  
  return (
    <Badge
      bg="" // Prevent Bootstrap from applying default bg-* classes
      className={`shop-order-status-badge shop-order-status-${statusClass} ${className}`}
      style={{
        backgroundColor: colors.backgroundColor,
        color: colors.color,
        borderColor: colors.borderColor,
        border: `1px solid ${colors.borderColor}`,
      } as React.CSSProperties}
    >
      {displayText}
    </Badge>
  );
};

interface LocationBadgeProps {
  locationName: string;
  className?: string;
}

export const LocationBadge: React.FC<LocationBadgeProps> = ({ locationName, className = '' }) => {
  const colors = BADGE_COLORS.LOCATION;
  return (
    <Badge
      bg="" // Prevent Bootstrap from applying default bg-* classes
      className={`shop-location-badge ${className}`}
      style={{
        backgroundColor: colors.backgroundColor,
        color: colors.color,
        borderColor: colors.borderColor,
        border: `1px solid ${colors.borderColor}`,
        fontFamily: 'Inter, Helvetica Neue, Arial, sans-serif',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '20px',
        textAlign: 'center',
        textTransform: 'none',
        minWidth: '80px',
        height: '32px',
        paddingLeft: '12px',
        paddingRight: '12px',
        paddingTop: '6px',
        paddingBottom: '6px',
        borderRadius: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
        marginRight: '8px',
        marginBottom: '8px'
      } as React.CSSProperties}
    >
      {locationName}
    </Badge>
  );
};

interface ProductBadgeProps {
  badge: 'BEST_SELLER' | 'NEW' | 'FEATURED' | 'TOP_PICKS' | 'TRENDING';
  label?: string;
  className?: string;
}

export const ProductBadge: React.FC<ProductBadgeProps> = ({ badge, label, className = '' }) => {
  const colors = BADGE_COLORS.PRODUCT[badge] || { backgroundColor: '#F1EEFD', color: '#7357EB', borderColor: '#7357EB' };

  const displayTextMap: { [key: string]: string } = {
    BEST_SELLER: 'Best Seller',
    NEW: 'New',
    FEATURED: 'Featured',
    TOP_PICKS: 'Top Picks',
    TRENDING: 'Trending',
  };

  const displayText = label || displayTextMap[badge] || badge;
  
  return (
    <Badge
      bg="" // Prevent Bootstrap from applying default bg-* classes
      className={`shop-product-badge shop-product-badge-${badge.toLowerCase().replace('_', '-')} ${className}`}
      style={{
        backgroundColor: colors.backgroundColor,
        color: colors.color,
        borderColor: colors.borderColor,
        border: `1px solid ${colors.borderColor}`,
        fontFamily: 'Inter, Helvetica Neue, Arial, sans-serif',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '20px',
        textAlign: 'center',
        textTransform: 'none',
        minWidth: '70px',
        height: '32px',
        paddingLeft: '12px',
        paddingRight: '12px',
        paddingTop: '6px',
        paddingBottom: '6px',
        borderRadius: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
        marginRight: '8px',
        marginBottom: '8px'
      } as React.CSSProperties}
    >
      {displayText}
    </Badge>
  );
};

interface CampaignStatusBadgeProps {
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED' | 'EXPIRED' | 'SCHEDULED' | 'INACTIVE';
  className?: string;
}

export const CampaignStatusBadge: React.FC<CampaignStatusBadgeProps> = ({ status, className = '' }) => {
  const colors = BADGE_COLORS.CAMPAIGN[status];
  
  // Display text mapping
  const displayTextMap: Record<CampaignStatusBadgeProps['status'], string> = {
    DRAFT: 'Draft',
    ACTIVE: 'Active',
    PAUSED: 'Paused',
    ARCHIVED: 'Archived',
    EXPIRED: 'Expired',
    SCHEDULED: 'Scheduled',
    INACTIVE: 'Inactive',
  };
  
  const displayText = displayTextMap[status];
  
  return (
    <Badge
      bg="" // Prevent Bootstrap from applying default bg-* classes
      className={`promos-campaign-status-badge promos-campaign-status-${status.toLowerCase()} ${className}`}
      style={{
        backgroundColor: colors.backgroundColor,
        color: colors.color,
        borderColor: colors.borderColor,
        border: `1px solid ${colors.borderColor}`,
      } as React.CSSProperties}
    >
      {displayText}
    </Badge>
  );
};

interface PromoStatusBadgeProps {
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED' | 'EXPIRED' | 'INACTIVE';
  className?: string;
}

export const PromoStatusBadge: React.FC<PromoStatusBadgeProps> = ({ status, className = '' }) => {
  const colors = BADGE_COLORS.PROMO[status];
  
  // Display text mapping
  const displayTextMap: Record<PromoStatusBadgeProps['status'], string> = {
    DRAFT: 'Draft',
    ACTIVE: 'Active',
    PAUSED: 'Paused',
    ARCHIVED: 'Archived',
    EXPIRED: 'Expired',
    INACTIVE: 'Inactive',
  };
  
  const displayText = displayTextMap[status];
  
  return (
    <Badge
      bg="" // Prevent Bootstrap from applying default bg-* classes
      className={`promos-promo-status-badge promos-promo-status-${status.toLowerCase()} ${className}`}
      style={{
        backgroundColor: colors.backgroundColor,
        color: colors.color,
        borderColor: colors.borderColor,
        border: `1px solid ${colors.borderColor}`,
      } as React.CSSProperties}
    >
      {displayText}
    </Badge>
  );
};

interface CampaignTypeBadgeProps {
  type: 'FESTIVAL' | 'SEASONAL' | 'PERFORMANCE' | 'PARTNER' | 'INTERNAL';
  className?: string;
}

export const CampaignTypeBadge: React.FC<CampaignTypeBadgeProps> = ({ type, className = '' }) => {
  const colors = BADGE_COLORS.CAMPAIGN_TYPE[type];
  
  // Display text mapping
  const displayTextMap: Record<CampaignTypeBadgeProps['type'], string> = {
    FESTIVAL: 'Festival',
    SEASONAL: 'Seasonal',
    PERFORMANCE: 'Performance',
    PARTNER: 'Partner',
    INTERNAL: 'Internal',
  };
  
  const displayText = displayTextMap[type];
  
  return (
    <Badge
      bg="" // Prevent Bootstrap from applying default bg-* classes
      className={`promos-campaign-type-badge promos-campaign-type-${type.toLowerCase()} ${className}`}
      style={{
        backgroundColor: colors.backgroundColor,
        color: colors.color,
        borderColor: colors.borderColor,
        border: `1px solid ${colors.borderColor}`,
      } as React.CSSProperties}
    >
      {displayText}
    </Badge>
  );
};

interface AuditLogActionBadgeProps {
  action: 'CREATED' | 'UPDATED' | 'PAUSED' | 'ACTIVATED' | 'EXPIRED' | 'DELETED' | 'INACTIVATED';
  className?: string;
}

export const AuditLogActionBadge: React.FC<AuditLogActionBadgeProps> = ({ action, className = '' }) => {
  const colors = BADGE_COLORS.AUDIT_LOG_ACTION[action];
  
  // Display text mapping
  const displayTextMap: Record<AuditLogActionBadgeProps['action'], string> = {
    CREATED: 'Created',
    UPDATED: 'Updated',
    PAUSED: 'Paused',
    ACTIVATED: 'Activated',
    EXPIRED: 'Expired',
    DELETED: 'Deleted',
    INACTIVATED: 'Inactivated',
  };
  
  const displayText = displayTextMap[action];
  
  return (
    <Badge
      bg="" // Prevent Bootstrap from applying default bg-* classes
      className={`promos-audit-log-action-badge promos-audit-log-action-${action.toLowerCase()} ${className}`}
      style={{
        backgroundColor: colors.backgroundColor,
        color: colors.color,
        borderColor: colors.borderColor,
        border: `1px solid ${colors.borderColor}`,
      } as React.CSSProperties}
    >
      {displayText}
    </Badge>
  );
};
